import EventEmmiter, { log } from './EventEmmiter';
import nativeInterface from './native';
import { readStore } from '../mobx/stores/rf';
import { bluetoothStore } from '../mobx/stores/bluetooth';
import {
  selectRandomFromArr,
  formatCommandNoEscape,
  stringToHex,
  autoCompleteCommand,
  parseProjParam,
  hexToString,
  hexToDecimal
} from '../js/utils';
import { taskStore } from '../mobx/stores/task';
import { WORK_MODE } from '../mobx/stores/work-mode';
import { globalStore } from '../mobx/stores/global';

const {
  readSerial,
  authRFID,
  sendRFIDCommand
} = nativeInterface;

// 暴露到全局的回调函数名前缀
export const CALLBACKS = {
  READSERIAL: 'readSerial',
  AUTHRFID: 'auth',
  SEND_COMMAND: 'sendCommand'
};

// 步骤描述，1：表示当前步骤；2：当前事件监听前缀
const STEPS = {
  READSERIAL: {
    step: 'readSerial',
    desc: '读RFID卡号',
  },
  AUTHRFID: {
    step: 'authRFID',
    desc: '密钥认证',
  },
  READ_PRJ_PARAM: {
    step: 'readProjectParam',
    desc: '读取项目参数',
  },
  READ_GEO_WORK_MODE: {
    step: 'readGeoWorkMode',
    desc: '读取地磁工作模式',
  },
  READ_IMEI: {
    step: 'readIMEI',
    desc: '读取IMEI',
  },
  READ_IMSI: {
    step: 'readIMSI',
    desc: '读取IMSI',
  },
  READ_ICCID: {
    step: 'readICCID',
    desc: '读取ICCID',
  },
  READ_MODE: {
    step: 'readMODE',
    desc: '读取地磁异常模式',
  },
  OPEN_BT: {
    step: 'openBluetooth',
    desc: '打开地磁蓝牙',
  },
  START_BTB: {
    step: 'startBluetoothBroadcast',
    desc: '开启地磁蓝牙广播',
  },
  STOP_BTB: {
    step: 'stopBluetoothBroadcast',
    desc: '关闭地磁蓝牙广播',
  },
};

// 可读数据的页码，格式：[起始页, 结束页]
const READ_PAGE = {
  projectParam: [23, 23], // 项目参数：版本，通讯类型，运营商，检测方式
  workMode: [225, 225],
  IMEI: [25, 28],
  IMSI: [29, 32],
  ICCID: [33, 37],
  MODE: [38, 39]
};

// 可写入cpu1的页码
// eslint-disable-next-line no-unused-vars
export const WRITE_CPU1_PAGE = [50, 75, 100, 125];
// 可写入cpu2的页码
export const WRITE_CPU2_PAGE = [160, 180, 200];

export class RFID extends EventEmmiter {
  constructor() {
    super();
    this.timer = null;
    // 当前执行步骤
    this.currentStep = '';
  }

  // 读RFID卡号
  readSerialNo(pure) {
    const session = taskStore.getSessionId();

    return this.execute({
      session,
      max: null,
      interval: 1000,
      step: STEPS.READSERIAL.step,
      executeFn: () => {
        readSerial(session, 'serial\r\n');
      },
    })
      .then(({ count, data }) => {
        if (!pure) {
          readStore.RFID = data;
        }
        log.setPreset({
          mac: data,
        });
        log.postSuccess({
          operate: STEPS.READSERIAL.desc,
          receive: data,
          receiveOrigin: data,
          count,
        });
        return Promise.resolve(data);
      })
      .catch((error) => {
        log.postError({
          operate: STEPS.READSERIAL.desc,
          error,
        });
        return Promise.reject(error);
      });
  }

  // 密钥认证
  auth() {
    const session = taskStore.getSessionId();

    return this.execute({
      session,
      max: 3,
      interval: 4000,
      step: STEPS.AUTHRFID.step,
      executeFn: () => {
        authRFID(session);
      },
    })
      .then(({ count, data }) => {
        log.postSuccess({
          operate: STEPS.AUTHRFID.desc,
          receive: data,
          receiveOrigin: data,
          count,
        });
        return Promise.resolve();
      })
      .catch((error) => {
        log.postError({
          operate: STEPS.AUTHRFID.desc,
          error,
        });
        return Promise.reject(error);
      });
  }

  /**
   * @description 通过RFID发卡器写入命令（读/写）
   * @param {string} step 步骤描述
   * @param {string} command 命令语句
   * @returns {Promise}
   * @memberof RFID
   */
  sendCommand(step, command) {
    const session = taskStore.getSessionId();

    return this.execute({
      session,
      max: 3,
      interval: 4000,
      step: step.step,
      executeFn: () => {
        sendRFIDCommand(session, command);
      },
    })
      .then(({ count, data }) => {
        log.postSuccess({
          operate: step.desc,
          send: command,
          receive: data,
          receiveOrigin: data,
          count,
        });
        return Promise.resolve(data);
      })
      .catch((error) => {
        log.postError({
          operate: step.desc,
          send: command,
          error,
        });
        return Promise.reject(error);
      });
  }

  /**
   * @description 执行写入命令
   * @param {string} step
   * @param {WRITE_CPU1_PAGE|WRITE_CPU2_PAGE} pageArr 可写入的对应cpu页码集合
   * @param {string} data 命令
   * @returns {Promise}
   * @memberof RFID
   */
  write(step, pageArr, data) {
    const that = this;
    const copyPage = [...pageArr];
    let full = 0;

    return new Promise((resolve, reject) => {
      // eslint-disable-next-line wrap-iife
      (function executeCommand() {
        if (copyPage.length === 0) {
          if (full === pageArr.length) {
            return reject(new Error('地磁分区已满，无法写入命令'));
          }
          return reject(new Error('执行超时'));
        }

        const page = selectRandomFromArr(copyPage);

        return that
          .read(
            {
              step: 'read page',
              desc: '读取分区',
            },
            [page, page]
          )
          .then((value) => {
            if (value === '00000000') {
              const command = autoCompleteCommand(formatCommandNoEscape(data));
              console.log(stringToHex(command));
              that
                .sendCommand(
                  step,
                  formatCommandNoEscape(`write ${page} ${stringToHex(command)}`)
                )
                .then((res) => {
                  resolve(res);
                })
                .catch((error) => {
                  reject(error);
                });
            } else {
              full++;
              executeCommand();
            }
          })
          .catch(() => {
            executeCommand();
          });
      })();
    });
  }

  forceWrite(step, page, data) {
    return this.sendCommand(
      step,
      formatCommandNoEscape(
        `write ${page} ${stringToHex(
          autoCompleteCommand(formatCommandNoEscape(data))
        )}`
      )
    );
  }

  /**
   * @description 执行读命令
   * @param {string} step 步骤描述
   * @param {Array} page 页码,格式：[开始页, 结束页]
   * @returns {Promise}
   * @memberof RFID
   */
  read(step, page) {
    console.log(formatCommandNoEscape(`read ${page[0]} ${page[1]}`));
    return this.sendCommand(
      step,
      formatCommandNoEscape(`read ${page[0]} ${page[1]}`)
    );
  }

  // 读取项目参数
  readProjectParam() {
    return this.read(STEPS.READ_PRJ_PARAM, READ_PAGE.projectParam)
      .then((data) => {
        const result = parseProjParam(data);
        if (!result) {
          return Promise.reject(new Error('获取项目参数失败'));
        }

        Object.keys(result).forEach((key) => {
          readStore.setProjParam(key, result[key]);
        });

        if (result.communicateType.value !== 'NB') {
          readStore.status = 'success';
        }

        return { skip: result.communicateType.value !== 'NB' };
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  // 读取地磁工作模式
  readGeoWorkMode(pure) {
    return this.read(STEPS.READ_GEO_WORK_MODE, READ_PAGE.workMode)
      .then((data) => {
        // console.log('225------------------', data);
        const mode = hexToDecimal(data.substr(-2));
        const manufacturer = data.substr(-4, 2);
        const isInitRadar = hexToDecimal(data.substr(-6, 2));

        // 工作模式允许获取失败或者工作模式未知
        // if (!mode) {
        //   return Promise.reject(new Error('获取地磁工作模式失败'));
        // }

        if (!manufacturer) {
          return Promise.reject(new Error('获取连接平台失败'));
        }

        globalStore.isInitRadar = !isInitRadar;

        if (!pure) {
          readStore.setManufacturer(manufacturer);

          globalStore.setMode(mode);
        }

        return Promise.resolve({
          manufacturer,
          mode,
        });
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  // 读取IMEI
  readIMEI() {
    return this.read(STEPS.READ_IMEI, READ_PAGE.IMEI)
      .then((data) => {
        console.log(`imei: ${data}`);
        readStore.rf.IMEI = hexToString(data.substr(0, data.length - 2));
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  // 读取IMSI
  readIMSI() {
    return this.read(STEPS.READ_IMSI, READ_PAGE.IMSI)
      .then((data) => {
        console.log(`imsi: ${data}`);
        readStore.rf.IMSI = hexToString(data.substr(0, data.length - 2));
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  // 读取ICCID
  readICCID() {
    return this.read(STEPS.READ_ICCID, READ_PAGE.ICCID)
      .then((data) => {
        console.log(`ICCID: ${data}`);
        readStore.rf.ICCID = hexToString(data);
        readStore.status = 'success';
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  // 读取地磁异常模式
  readMode() {
    return this.read(STEPS.READ_MODE, READ_PAGE.MODE)
      .then((data) => {
        console.log(`异常模式: ${data.substring(0, 8)}`);
        console.log(`进入异常模式时间：${data.substring(8, 16)}`);
        readStore.rf.MODE = data.substring(0, 8);
        readStore.rf.MODETIME = data.substring(8, 16);
        readStore.status = 'success';
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  // 唤醒地磁蓝牙
  openBluetooth() {
    readStore.status = 'success';
    return this.write(STEPS.OPEN_BT, WRITE_CPU2_PAGE, 'on bt 60')
      .then(() => {
        bluetoothStore.status = 'success';
        return Promise.resolve();
      })
      .catch((error) => {
        bluetoothStore.status = 'error';
        return Promise.reject(error);
      });
  }

  // 唤醒地磁蓝牙
  startBluetoothBroadcast(time) {
    return this.write(STEPS.START_BTB, WRITE_CPU1_PAGE, `btb on ${time}`);
  }

  // 唤醒地磁蓝牙
  stopBluetoothBroadcast() {
    return this.write(STEPS.STOP_BTB, WRITE_CPU1_PAGE, 'btb off');
  }
}

export default new RFID();
